import { useState } from "react";
import type { ReactNode } from "react";
import {
  Avatar,
  Badge,
  Button,
  Card,
  Group,
  LoadingOverlay,
  RingProgress,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
} from "@mantine/core";
import {
  IconArrowDownRight,
  IconArrowUpRight,
  IconBoxMultiple,
  IconChecklist,
  IconClock,
  IconGauge,
  IconPlayerPlay,
  IconTarget,
} from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { mvpApiListJobs, JobStatus } from "@augmodo/lebron-browser-client";
import {
  useDefaultTaskType,
  useMyUserMetrics,
  useUserMetrics,
  useViewableUsers,
  type InsightsUserBriefSchema,
  type MetricsPeriod,
  type UserMetricsSchema,
} from "@/utils/userInsights";
import {
  boxesPerHour,
  boxesPerShift,
  COVERAGE_BETA_TASK_TYPE,
  OOS_CONFIRMATION_TASK_TYPE,
} from "@/utils/profile-metrics";
import { RejectedQaTasksSection } from "@/components/RejectedQaTasksSection";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { setIsPlayMode } from "@/store";
import { TASK_TYPE_ROUTE, PLAY_MODE_JOB_TYPE } from "@/config/data";
import type { ListJobsQuery } from "@/hooks/useFetchNextPlayModeJob";

const TASK_TYPE_OPTIONS = [
  { value: OOS_CONFIRMATION_TASK_TYPE, label: "OOS Confirmation" },
  { value: COVERAGE_BETA_TASK_TYPE, label: "Coverage Beta" },
  { value: "COVERAGE_REVIEW", label: "Coverage Review" },
];

const PERIOD_OPTIONS = [
  { label: "All time", value: "all_time" },
  { label: "Last 30 days", value: "last_30d" },
  { label: "Last week", value: "last_7d" },
];

const MIN_REVIEWED_FOR_PRECISION = 10;

function initials(email: string): string {
  const name = email.split("@")[0] ?? email;
  const parts = name.split(/[.\-_]+/).filter(Boolean);
  const letters = (parts.length >= 2 ? [parts[0], parts[1]] : [name]).map((p) =>
    p.charAt(0).toUpperCase(),
  );
  return letters.join("").slice(0, 2) || "?";
}

function scoreGrade(score: number): {
  grade: string;
  color: string;
  label: string;
} {
  if (score >= 90) return { grade: "A", color: "teal", label: "Excellent" };
  if (score >= 80) return { grade: "B", color: "green", label: "Good" };
  if (score >= 70) return { grade: "C", color: "yellow", label: "Fair" };
  if (score >= 60) return { grade: "D", color: "orange", label: "Needs work" };
  return { grade: "F", color: "red", label: "Needs improvement" };
}

interface Comparison {
  avgText: string;
  deltaText: string;
  better: boolean;
}

interface MetricCardProps {
  icon: ReactNode;
  color: string;
  label: string;
  value: string;
  caption: string;
  comparison?: Comparison;
  placeholder?: string;
}

function MetricCard({
  icon,
  color,
  label,
  value,
  caption,
  comparison,
  placeholder,
}: MetricCardProps) {
  return (
    <Card withBorder radius="lg" padding="lg" className="shadow-sm">
      <Group justify="space-between" align="center" wrap="nowrap">
        <Text size="sm" c="dimmed" fw={500}>
          {label}
        </Text>
        <ThemeIcon variant="light" color={color} size={38} radius="md">
          {icon}
        </ThemeIcon>
      </Group>
      {placeholder != null ? (
        <Text fz={18} c="dimmed" mt="md" lh={1.3}>
          {placeholder}
        </Text>
      ) : (
        <Text fz={36} fw={700} mt="md" lh={1}>
          {value}
        </Text>
      )}
      <Text size="xs" c="dimmed" mt={8}>
        {caption}
      </Text>
      {!placeholder && comparison && (
        <Group justify="space-between" align="center" mt="md" wrap="nowrap">
          <Text size="xs" c="dimmed">
            Team avg {comparison.avgText}
          </Text>
          <Badge
            size="sm"
            radius="sm"
            variant="light"
            color={comparison.better ? "teal" : "red"}
            leftSection={
              comparison.better ? (
                <IconArrowUpRight size={12} />
              ) : (
                <IconArrowDownRight size={12} />
              )
            }
          >
            {comparison.deltaText}
          </Badge>
        </Group>
      )}
    </Card>
  );
}

function NoMetricsCard() {
  return (
    <Card
      withBorder
      radius="lg"
      padding="xl"
      mb="lg"
      className="shadow-sm"
      miw={360}
      mih={200}
    >
      <Text ta="center" c="dimmed" mt="xl">
        No metrics found for this task type yet.
      </Text>
    </Card>
  );
}

const signed = (n: number, suffix = "") =>
  `${n >= 0 ? "+" : "-"}${Math.abs(n)}${suffix}`;

/** A precision card, held at a placeholder until enough work has been QA'd. */
function precisionCard(
  label: string,
  caption: string,
  precision: number,
  teamPrecision: number,
  reviewed: number,
): MetricCardProps {
  const hasEnoughReviewed = reviewed >= MIN_REVIEWED_FOR_PRECISION;
  // A 0 team average means no teammate has enough QA'd work to count toward
  // it yet, not a 0% team.
  const hasTeamPrecision = teamPrecision > 0;
  return {
    icon: <IconTarget size={22} />,
    color: "blue",
    label,
    value: `${Math.round(precision * 100)}%`,
    caption,
    comparison:
      hasEnoughReviewed && hasTeamPrecision
        ? {
            avgText: `${Math.round(teamPrecision * 100)}%`,
            deltaText: signed(
              Math.round((precision - teamPrecision) * 100),
              "%",
            ),
            better: precision >= teamPrecision,
          }
        : undefined,
    placeholder: hasEnoughReviewed ? undefined : "Not enough QA'd yet",
  };
}

function taskMetricCards(data: UserMetricsSchema): MetricCardProps[] {
  const avg = data.averages;
  return [
    precisionCard(
      "Precision",
      "Share of reviewed tasks marked correct",
      data.precision,
      avg.precision,
      data.reviewed_tasks ?? 0,
    ),
    {
      icon: <IconChecklist size={22} />,
      color: "blue",
      label: "Avg tasks / shift",
      value: `${Math.round(data.avg_tasks_per_shift)}`,
      caption: "Tasks completed per shift",
      comparison: {
        avgText: `${Math.round(avg.avg_tasks_per_shift)}`,
        deltaText: signed(
          Math.round(data.avg_tasks_per_shift - avg.avg_tasks_per_shift),
        ),
        better: data.avg_tasks_per_shift >= avg.avg_tasks_per_shift,
      },
    },
    {
      icon: <IconClock size={22} />,
      color: "indigo",
      label: "Avg task time",
      value: `${data.avg_task_time.toFixed(1)}s`,
      caption: "Average time spent per task",
      comparison: {
        avgText: `${avg.avg_task_time.toFixed(1)}s`,
        deltaText: signed(
          Number((data.avg_task_time - avg.avg_task_time).toFixed(1)),
          "s",
        ),
        better: data.avg_task_time <= avg.avg_task_time,
      },
    },
  ];
}

// Coverage Beta is scored per box, not per task (a task has many boxes),
// mirroring user-insights' _SCORING_OVERRIDES. Cards follow the OOS order:
// precision, per-shift volume, then speed.
function coverageBetaMetricCards(data: UserMetricsSchema): MetricCardProps[] {
  const avg = data.averages;
  const perShift = boxesPerShift(data);
  const teamPerShift = avg.avg_boxes_per_shift ?? 0;
  const perHour = boxesPerHour(data.avg_box_time);
  const teamPerHour = boxesPerHour(avg.avg_box_time);
  return [
    precisionCard(
      "Box precision",
      "Share of QA-reviewed boxes marked correct",
      data.box_precision ?? 0,
      avg.box_precision ?? 0,
      // QA'd submissions, the same gate user-insights uses for Beta.
      data.reviewed_tasks ?? 0,
    ),
    {
      icon: <IconBoxMultiple size={22} />,
      color: "blue",
      label: "Avg boxes / shift",
      value: `${Math.round(perShift)}`,
      caption: "Boxes labeled per shift",
      comparison: teamPerShift
        ? {
            avgText: `${Math.round(teamPerShift)}`,
            deltaText: signed(Math.round(perShift - teamPerShift)),
            better: perShift >= teamPerShift,
          }
        : undefined,
    },
    {
      icon: <IconGauge size={22} />,
      color: "indigo",
      label: "Avg boxes / hour",
      value: `${Math.round(perHour)}`,
      caption: "Boxes labeled per hour of task time",
      comparison: teamPerHour
        ? {
            avgText: `${Math.round(teamPerHour)}`,
            deltaText: signed(Math.round(perHour - teamPerHour)),
            better: perHour >= teamPerHour,
          }
        : undefined,
    },
  ];
}

function MetricsDisplay({
  data,
  taskType,
}: {
  data: UserMetricsSchema;
  taskType: string;
}) {
  const score = data.overall_score;
  const { grade, color, label } = scoreGrade(score);

  const isCoverageBeta = taskType === COVERAGE_BETA_TASK_TYPE;
  const cards = isCoverageBeta
    ? coverageBetaMetricCards(data)
    : taskMetricCards(data);
  const gradeBlurb = isCoverageBeta
    ? "A weighted blend of box precision, tasks per shift, and time per box."
    : "A weighted blend of precision, tasks per shift, and average task speed.";

  return (
    <>
      <Card withBorder radius="lg" padding={40} mb="lg" className="shadow-sm">
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={48}>
          <Group justify="center">
            <RingProgress
              size={200}
              thickness={18}
              roundCaps
              sections={[{ value: score, color }]}
              label={
                <Stack gap={0} align="center" justify="center">
                  <Text fz={72} fw={800} lh={1} c={color}>
                    {grade}
                  </Text>
                  <Text size="sm" c="dimmed" mt={4}>
                    {Math.round(score)} / 100
                  </Text>
                </Stack>
              }
            />
          </Group>
          <Stack gap="sm" justify="center" align="center">
            <Text
              size="sm"
              c="dimmed"
              tt="uppercase"
              fw={700}
              lts={0.5}
              ta="center"
            >
              Overall grade
            </Text>
            <Badge size="xl" variant="light" color={color} radius="sm">
              {label}
            </Badge>
            <Text size="sm" c="dimmed" maw={320} ta="center">
              {gradeBlurb}
            </Text>
          </Stack>
        </SimpleGrid>
      </Card>

      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="lg">
        {cards.map((card) => (
          <MetricCard key={card.label} {...card} />
        ))}
      </SimpleGrid>
    </>
  );
}

function OtherUserMetrics({
  userId,
  taskType,
  period,
}: {
  userId: string;
  taskType: string;
  period: MetricsPeriod;
}) {
  // Fetch all three periods in parallel so switching the segmented control is instant.
  const allTime = useUserMetrics(userId, taskType, "all_time");
  const last30d = useUserMetrics(userId, taskType, "last_30d");
  const last7d = useUserMetrics(userId, taskType, "last_7d");
  const byPeriod = { all_time: allTime, last_30d: last30d, last_7d: last7d };
  const { data, isLoading, isError } = byPeriod[period];
  const noMetrics =
    !isLoading && (isError || !data || data.total_tasks_completed === 0);

  return (
    <div className="relative">
      <LoadingOverlay visible={isLoading} zIndex={5} />
      {noMetrics ? (
        <NoMetricsCard />
      ) : (
        data && <MetricsDisplay data={data} taskType={taskType} />
      )}
    </div>
  );
}

const CAN_VIEW_OTHERS = new Set(["augmodo", "team_lead"]);

export const ProfilePage = () => {
  // null = open on the labeler's main workflow (see useDefaultTaskType).
  const [taskTypeOverride, setTaskTypeOverride] = useState<string | null>(null);
  const [period, setPeriod] = useState<MetricsPeriod>("last_7d");
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [startLabelingLoading, setStartLabelingLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const selectedRetailerId = useAppSelector(
    (state) => state.oos.selectedRetailerId,
  );

  // The viewer's role, email and vendor come from a query whose key never
  // changes, so they don't blink out while another type or period loads (which
  // would briefly drop a manager back to their own profile). The default-type
  // lookup below makes this same request.
  const { data: me } = useMyUserMetrics(OOS_CONFIRMATION_TASK_TYPE, "last_30d");
  const viewerRole = me?.role ?? null;
  const canViewOthers = viewerRole !== null && CAN_VIEW_OTHERS.has(viewerRole);

  const { data: viewableUsers } = useViewableUsers();

  const viewingOther = canViewOthers && selectedUserId !== null;
  const displayedUser: InsightsUserBriefSchema | undefined = viewingOther
    ? viewableUsers?.find((u) => u.id === selectedUserId)
    : undefined;

  // Null until the displayed labeler's main workflow is known, so the page
  // waits rather than rendering OOS and then swapping.
  const { taskType: defaultTaskType } = useDefaultTaskType(
    viewingOther ? selectedUserId : null,
  );
  const taskType = taskTypeOverride ?? defaultTaskType;

  // Fetch all three periods in parallel so switching the segmented control is instant.
  const myTaskType = viewingOther ? null : taskType;
  const myMetricsAllTime = useMyUserMetrics(myTaskType, "all_time");
  const myMetricsLast30d = useMyUserMetrics(myTaskType, "last_30d");
  const myMetricsLast7d = useMyUserMetrics(myTaskType, "last_7d");
  const myMetricsByPeriod = {
    all_time: myMetricsAllTime,
    last_30d: myMetricsLast30d,
    last_7d: myMetricsLast7d,
  };
  const { data: myData, isLoading: myLoading } = myMetricsByPeriod[period];

  const email = viewingOther ? (displayedUser?.email ?? "") : (me?.email ?? "");
  const vendor = viewingOther
    ? (displayedUser?.vendor ?? "")
    : (me?.vendor ?? "");

  const userPickerData = [
    { value: "__me__", label: "My profile" },
    ...(viewableUsers ?? [])
      .filter((u) => u.email !== me?.email)
      .map((u) => ({ value: u.id, label: u.email })),
  ];

  const noMyMetrics =
    !myLoading && (!myData || myData.total_tasks_completed === 0);

  // QA section assignee: pass email of the selected user, or undefined (→ "me") for self
  const qaAssignee = viewingOther
    ? (displayedUser?.email ?? undefined)
    : undefined;

  let metricsContent: ReactNode;
  if (taskType === null) {
    metricsContent = (
      <div className="relative min-h-[200px]">
        <LoadingOverlay visible zIndex={5} />
      </div>
    );
  } else if (viewingOther) {
    metricsContent = (
      <OtherUserMetrics
        key={selectedUserId!}
        userId={selectedUserId!}
        taskType={taskType}
        period={period}
      />
    );
  } else {
    metricsContent = (
      <div className="relative">
        <LoadingOverlay visible={myLoading} zIndex={5} />
        {noMyMetrics ? (
          <NoMetricsCard />
        ) : (
          myData && <MetricsDisplay data={myData} taskType={taskType} />
        )}
      </div>
    );
  }

  const handleStartLabeling = async () => {
    if (taskType === null) return;
    // Coverage Beta work is served from /coverage, which opens the Beta UI
    // when useCoverageBetaEnabled says so (CoverageRoute in App.tsx).
    const route =
      taskType === COVERAGE_BETA_TASK_TYPE
        ? "coverage"
        : TASK_TYPE_ROUTE[taskType];
    const config = route ? PLAY_MODE_JOB_TYPE[route] : undefined;
    if (!config) return;

    setStartLabelingLoading(true);
    try {
      const response = await mvpApiListJobs({
        query: {
          type: config.jobType,
          status: [JobStatus.NEEDS_REVIEW],
          page_size: 10,
          ordering: "created_at",
          page: 1,
          // Entering play mode, so skip jobs holding a task this reviewer
          // must not be served (see useFetchNextPlayModeJob).
          servable_to_me: true,
          ...(selectedRetailerId
            ? { atlas_retailer_id: selectedRetailerId }
            : {}),
        } satisfies ListJobsQuery,
      });
      const jobs = (response.data as any)?.jobs ?? [];
      if (jobs.length > 0) {
        dispatch(setIsPlayMode(true));
        navigate(`/jobs/${jobs[0].id}/${config.route}`);
      }
    } catch (e) {
      console.error("Failed to start labeling:", e);
    } finally {
      setStartLabelingLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl min-w-[896px] px-4 py-8">
      {/* Header */}
      <Stack gap="md" mb="xl">
        <Group gap="md" align="center" wrap="nowrap" justify="space-between">
          <Group gap="md" align="center" wrap="nowrap">
            <Avatar color="initials" radius="xl" size={64} name={email}>
              {email ? initials(email) : null}
            </Avatar>
            <Stack gap={6}>
              <Text fz={26} fw={700} lh={1.1}>
                {email || "—"}
              </Text>
              {vendor && (
                <Badge
                  variant="light"
                  color="grape"
                  radius="sm"
                  w="fit-content"
                >
                  {vendor}
                </Badge>
              )}
            </Stack>
          </Group>

          <Button
            size="sm"
            variant="light"
            color="blue"
            leftSection={<IconPlayerPlay size={14} />}
            loading={startLabelingLoading}
            disabled={taskType === null}
            onClick={handleStartLabeling}
          >
            Start Labeling
          </Button>
        </Group>

        <Group gap="sm" wrap="wrap" justify="space-between">
          <Group gap="sm" wrap="wrap">
            <Select
              data={userPickerData}
              value={selectedUserId ?? "__me__"}
              onChange={(v) => {
                setSelectedUserId(v === "__me__" ? null : (v ?? null));
                // Open each labeler on their own main workflow.
                setTaskTypeOverride(null);
              }}
              searchable
              size="sm"
              w={220}
              allowDeselect={false}
              placeholder="Select labeler"
              disabled={!viewableUsers}
            />
            <Select
              data={TASK_TYPE_OPTIONS}
              value={taskType}
              onChange={(v) => {
                if (v) setTaskTypeOverride(v);
              }}
              size="sm"
              w={190}
              allowDeselect={false}
              placeholder="Task type"
              disabled={taskType === null}
            />
          </Group>

          <SegmentedControl
            data={PERIOD_OPTIONS}
            value={period}
            onChange={(v) => setPeriod(v as MetricsPeriod)}
            size="sm"
          />
        </Group>
      </Stack>

      {/* Metrics */}
      {metricsContent}

      {/* QA Review — shows for both self and other users (managers use this for 1-on-1s) */}
      {taskType !== null && (
        <RejectedQaTasksSection
          key={`${taskType}:${qaAssignee ?? "me"}`}
          assignee={qaAssignee}
          taskType={taskType}
        />
      )}
    </div>
  );
};
